import type { CsvValue } from './csv';

/** A condition on one exported column, applied to the raw cell values. */
export type ExportOp = 'contains' | 'equals' | 'notEquals' | 'gte' | 'lte' | 'empty' | 'notEmpty';

export interface ExportFilter {
  /** Index of the column in the export's headers. */
  column: number;
  op: ExportOp;
  value: string;
}

export const EXPORT_OPERATORS: readonly { id: ExportOp; label: string; needsValue: boolean }[] = [
  { id: 'contains', label: 'contains', needsValue: true },
  { id: 'equals', label: 'equals', needsValue: true },
  { id: 'notEquals', label: 'does not equal', needsValue: true },
  { id: 'gte', label: 'at least (or on or after)', needsValue: true },
  { id: 'lte', label: 'at most (or on or before)', needsValue: true },
  { id: 'empty', label: 'is empty', needsValue: false },
  { id: 'notEmpty', label: 'is not empty', needsValue: false },
];

const needsValue = (op: ExportOp) => EXPORT_OPERATORS.find((o) => o.id === op)?.needsValue ?? true;

/** A filter whose value is still blank does not narrow the rows. */
export const isActiveFilter = (filter: ExportFilter) =>
  !needsValue(filter.op) || filter.value.trim() !== '';

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
const YES = new Set(['true', 'yes', 'y', '1']);
const NO = new Set(['false', 'no', 'n', '0']);

const isEmpty = (value: CsvValue): value is null | undefined | '' =>
  value === null || value === undefined || value === '';

/** The typed number of the text, or null when it is not one ("1,5" counts as 1.5). */
function numberOf(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function same(value: string | number | boolean, text: string): boolean {
  if (typeof value === 'number') return numberOf(text) === value;
  if (typeof value === 'boolean') {
    const t = text.trim().toLowerCase();
    return value ? YES.has(t) : NO.has(t);
  }
  return value.trim().toLowerCase() === text.trim().toLowerCase();
}

/** Sign of value − text: numbers by value, text (ISO dates included) by collation; null when unrelated. */
function compare(value: string | number | boolean, text: string): number | null {
  if (typeof value === 'number') {
    const n = numberOf(text);
    return n === null ? null : value - n;
  }
  if (typeof value === 'boolean')
    return Number(value) - (YES.has(text.trim().toLowerCase()) ? 1 : 0);
  return collator.compare(value.trim(), text.trim());
}

/** Whether a cell satisfies the condition; a blank value in a condition that needs one matches all. */
export function matches(value: CsvValue, filter: Pick<ExportFilter, 'op' | 'value'>): boolean {
  if (!isActiveFilter({ column: 0, ...filter })) return true;
  const text = filter.value;
  switch (filter.op) {
    case 'empty':
      return isEmpty(value);
    case 'notEmpty':
      return !isEmpty(value);
    case 'contains':
      return !isEmpty(value) && String(value).toLowerCase().includes(text.trim().toLowerCase());
    case 'equals':
      return !isEmpty(value) && same(value, text);
    case 'notEquals':
      return isEmpty(value) || !same(value, text);
    case 'gte': {
      const c = isEmpty(value) ? null : compare(value, text);
      return c !== null && c >= 0;
    }
    case 'lte': {
      const c = isEmpty(value) ? null : compare(value, text);
      return c !== null && c <= 0;
    }
  }
}

/** Whether a row (its exported cells) satisfies every filter. */
export const matchesAll = (cells: readonly CsvValue[], filters: readonly ExportFilter[]) =>
  filters.every((filter) => matches(cells[filter.column], filter));

/** "Offer contains Business", "Risk is empty". */
export function describeExportFilter(filter: ExportFilter, headers: readonly string[]): string {
  const header = headers[filter.column] ?? `Column ${filter.column + 1}`;
  const op = EXPORT_OPERATORS.find((o) => o.id === filter.op);
  const label = op?.label.replace(/ \(.*\)$/, '') ?? filter.op;
  return op?.needsValue ? `${header} ${label} ${filter.value.trim()}` : `${header} ${label}`;
}
